import i18nLib, { InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import { english } from '~web/i18n/english';
import { french } from '~web/i18n/french';
import { log } from '~common/service/logger';
import { stores } from '~web/store';
import { UserPreferencesStore } from '~web/store/user-preferences-store';
import { spanish } from '~web/i18n/spanish';

interface Translation {
  [locale: string]: any;
}

interface TranslationResource {
  [key: string]: Translation;
}

/**
 * Locale enum - not trimmed
 */
export enum Locale {
  ENGLISH_US = 'en-US',
  ENGLISH_CA = 'en-CA',
  FRENCH_CA = 'fr-CA',
  SPANISH_US = 'es-US',
}

/**
 * Locale trimmed to language enum
 */
export enum Language {
  ENGLISH = 'en',
  FRENCH = 'fr',
  SPANISH = 'es',
}

/**
 * Removes the country code in a locale
 * In other words, it removes everything after "-" and keep only the 2 letter locale
 * i.e. 'en-US' will become 'en'
 * @param locale
 */
export const getLanguageFromLocale = (locale: string): Language => {
  if (locale) {
    const index = locale.indexOf('-');
    if (index > 0) {
      return locale.substring(0, index) as Language;
    }
    return locale as Language;
  }
  return locale as Language;
};

/**
 * Returns an array of supported locales
 */
export const supportedLocales: Locale[] = [
  Locale.ENGLISH_US,
  Locale.ENGLISH_CA,
  Locale.FRENCH_CA,
  Locale.SPANISH_US,
];

/**
 * Returns an array of supported languages
 */
export const supportedLanguages: Language[] = [Language.ENGLISH, Language.FRENCH, Language.SPANISH];

const buildResourcesConfig = (translations: Translation): TranslationResource => {
  const resources: TranslationResource = {};
  Object.keys(translations).forEach((locale) => {
    resources[locale] = {
      translations: translations[locale],
    };
  });
  return resources;
};

export const createI18nInitConfig = (translations: Translation): InitOptions => {
  return {
    fallbackLng: Language.ENGLISH,
    ns: ['translations'],
    defaultNS: 'translations',
    resources: buildResourcesConfig(translations),
    interpolation: {
      escapeValue: false,
      format: (value: any): string => value,
    },

    nonExplicitWhitelist: true,
    whitelist: supportedLanguages,

    react: {
      wait: false,
    },

    detection: {
      order: ['cookie', 'localStorage', 'navigator', 'htmlTag', 'path'],
      lookupLocalStorage: 'userLocale',
      excludeCacheFor: supportedLocales,
    },
  };
};

export const i18n = i18nLib;

/**
 * Returns the current user locale
 */
export const getUserLocale = (): Locale => i18n.language as Locale;

/**
 * Returns the current user language
 */
export const getUserLanguage = (): Language => getLanguageFromLocale(getUserLocale());

/**
 * Sets the locale of i18n library
 */
export const setLocale = (
  locale: Locale,
  onSuccess?: (locale: string) => void,
  onError?: (locale: string) => void,
): Promise<any> =>
  i18nLib.changeLanguage(locale, (error) => {
    if (error) {
      if (onError) {
        onError(locale);
      } else {
        log.error(`Tried to change locale to ${locale} and got an error: ${error.toString()}`);
      }
    }

    dayjs.locale(getUserLanguage());
    if (onSuccess) {
      onSuccess(locale);
    }
  });

export const initializeI18N = (): void => {
  i18nLib
    .use(LanguageDetector)
    .use(initReactI18next)
    .init(
      createI18nInitConfig({
        en: english,
        fr: french,
        es: spanish,
      }),
    )
    .then(() => {
      const userPreferencesStore = stores.userPreferencesStore as UserPreferencesStore;
      const locale = i18n.language as Locale;
      const language = getUserLanguage();
      if (supportedLocales.includes(locale)) {
        userPreferencesStore.setLocale(locale);
      } else {
        if (language === Language.FRENCH) {
          userPreferencesStore.setLocale(Locale.FRENCH_CA);
        } else {
          userPreferencesStore.setLocale(Locale.ENGLISH_US);
        }
      }
      dayjs.locale(language);
    });
};

export const t = i18nLib.t.bind(i18n);



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/web/src/js/i18n/index.ts